import { AlertTriangle, Database } from "lucide-react";

/**
 * Shared component that renders BOTH stock values side-by-side:
 * - System (Sistema de Gestão / MongoDB calculated): main value
 * - Yzidro (ERP PostgreSQL): secondary line
 *
 * Props:
 *   product      - object with at least { current_stock, pg_saldo, pg_sync_at, unit }
 *   size         - "sm" | "md" | "lg" (default "md")
 *   inline       - if true, render on one line (compact table cells)
 *   showLabel    - if true, prefix labels "Sistema:" / "ERP:"
 *   overrideSystem - override the system value (some components use salesRhythm.current_stock)
 */
export function StockDualValue({
  product,
  size = "md",
  inline = false,
  showLabel = false,
  overrideSystem = undefined,
}) {
  const sys = overrideSystem !== undefined
    ? Number(overrideSystem || 0)
    : Number(product?.current_stock ?? product?.total_stock ?? 0);
  const pgRaw = product?.pg_saldo;
  const hasPg = pgRaw !== null && pgRaw !== undefined;
  const pg = hasPg ? Number(pgRaw) : null;
  const unit = product?.unit || "un";

  const diff = hasPg ? sys - pg : 0;
  const absDiff = Math.abs(diff);
  const maxVal = Math.max(Math.abs(sys), Math.abs(pg || 0));
  const isDivergent = hasPg && absDiff > 1 && absDiff / (maxVal || 1) > 0.01;

  // Staleness: > 24h
  let staleHours = null;
  if (product?.pg_sync_at) {
    const syncedAt = new Date(product.pg_sync_at).getTime();
    if (!isNaN(syncedAt)) staleHours = (Date.now() - syncedAt) / 36e5;
  }
  const isStale = staleHours !== null && staleHours > 24;

  const fmt = (v) => {
    if (v === null || v === undefined) return "—";
    const n = Number(v);
    if (Number.isInteger(n)) return n.toLocaleString("pt-BR");
    return n.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
  };

  const mainSize =
    size === "lg" ? "text-2xl font-bold" :
    size === "sm" ? "text-sm font-semibold" :
    "text-base font-semibold";
  const subSize = size === "lg" ? "text-sm" : "text-xs";
  const mainColor = sys <= 0 ? "text-red-600" : "text-slate-900";
  const erpColor = !hasPg
    ? "text-slate-400"
    : isDivergent
      ? (diff > 0 ? "text-amber-600" : "text-red-600")
      : "text-slate-500";

  if (inline) {
    return (
      <span className="inline-flex items-baseline gap-2" data-testid="stock-dual-inline">
        <span className={`${mainSize} ${mainColor}`} title="Sistema (Gestão)" data-testid="stock-sys-value">
          {fmt(sys)}
        </span>
        <span className={`${subSize} ${erpColor}`} title={hasPg ? "ERP Yzidro" : "Sem dado ERP"} data-testid="stock-erp-value">
          {showLabel ? "ERP: " : "· "}{fmt(pg)}
        </span>
        {isDivergent && (
          <AlertTriangle
            className={`w-3 h-3 ${diff > 0 ? "text-amber-600" : "text-red-600"}`}
            title={`Divergência: ${diff > 0 ? "+" : ""}${fmt(diff)} ${unit}`}
            data-testid="stock-divergence-icon"
          />
        )}
      </span>
    );
  }

  return (
    <div className="flex flex-col leading-tight" data-testid="stock-dual-block">
      <div className={`${mainSize} ${mainColor} flex items-center gap-1.5`}>
        <span data-testid="stock-sys-value">{fmt(sys)}</span>
        {showLabel && <span className={`${subSize} font-normal text-slate-400`}>Sistema</span>}
      </div>
      <div className={`${subSize} ${erpColor} flex items-center gap-1`}>
        <Database className="w-3 h-3 opacity-60" />
        <span data-testid="stock-erp-value">
          {showLabel ? "ERP: " : ""}{hasPg ? fmt(pg) : "sem dado"}
        </span>
        {isDivergent && (
          <span
            className={`ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
              diff > 0 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
            }`}
            title="Divergência entre Sistema e ERP"
            data-testid="stock-divergence-badge"
          >
            <AlertTriangle className="w-2.5 h-2.5" />
            {diff > 0 ? "+" : ""}{fmt(diff)}
          </span>
        )}
        {isStale && hasPg && (
          <span
            className="ml-1 text-[10px] text-slate-400"
            title={`Última sincronização ERP: ${new Date(product.pg_sync_at).toLocaleString("pt-BR")}`}
          >
            (defasado)
          </span>
        )}
      </div>
    </div>
  );
}

export default StockDualValue;
